import React, { useEffect, useRef, useState } from "react";
import { Form, Row, Col, Dropdown } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./style.css";
import caretdown from "../../../assets/images/caretdown.svg";
import DatePicker from "react-datepicker";
import calendarIcon from "../../../assets/images/calendarIcon.svg";
import { dateConversion } from "../../../utils/formatDate";
import {default as CommonSelectField} from "../../SummaryReportCoC/FscProductGroupList/SelectField";

interface CarDetailProps {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  carData: any;
  certifiedSiteData: any[];
  disabled : boolean
}

const SelectField: React.FC<{
  label: string;
  data: Array<any>;
  selectedValue: string;
  disabled: boolean
  onChange: (value: string) => void;
}> = ({ label, data, selectedValue,disabled, onChange }) => (
  <Dropdown>
    <Dropdown.Toggle disabled = {disabled}
      className={`custom-dropdown ${selectedValue ? "selected-value-label" : "dropdown-placeholder"}`}
    >
      {selectedValue || label}
      <img
        style={{
          width: "20px",
          height: "20px",
          position: "absolute",
          right: "3px",
          fill: "gray",
        }}
        src={caretdown}
        alt="Dropdown icon"
      />
    </Dropdown.Toggle>

    <Dropdown.Menu>
      {data.map((option) => (
        <Dropdown.Item
          key={option.id}
          as="button"
          active={option.label === selectedValue}
          onClick={(e) => {
            e.preventDefault();
            onChange(option.value);
          }}
          className="menu-list"
        >
          {option.label}
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  </Dropdown>
);


const CarDetail: React.FC<CarDetailProps> = ({ formData, setFormData, carData, certifiedSiteData,disabled }) => {
  const data = JSON.parse(localStorage.getItem('lookupData') as string);
  const [selectedValues, setSelectedValues] = useState<{
    scheme: string;
    primaryStandardReference: string;
    status: string;
    submitClosedStatus: boolean;
  }>({
    scheme: "",
    primaryStandardReference: "",
    status: "",
    submitClosedStatus: false,
  });
  const [issueDate, setIssueDate] = useState<any>(null);
 
  const option = {
    scheme: data?.scheme
      ? Object.keys(data.scheme).map((key, index) => ({
          id: index + 1,
          label: data.scheme[key],
          value: key,
        }))
      : [], // return empty array if data.scheme is undefined or null
    primaryStandardReference: data?.standards
      ? Object.keys(data.standards).map((key, index) => ({
          id: index + 1,
          label: data.standards[key],
          value: key,
        }))
      : [], // return empty array if data.standards is undefined or null
    status: data?.status
      ? data.status.map((status: any, index: number) => ({
          id: index + 1,
          label: status,
          value: status,
        }))
      : []
  };
  
  const initialFormDataRef = useRef<any>(null);

  const handleSelectChange = (field: string, value: string) => {
    setSelectedValues((prevValues) => {
      const updatedValues = { ...prevValues, [field]: value };
      setFormData((prevFormData: { CarDetail: any }) => ({
        ...prevFormData,
        CarDetail: {
          ...prevFormData.CarDetail,
          [field]: value,
        },
      }));
      return updatedValues;
    });
  };

  useEffect(() => {
    if (formData.CarDetail.issueDate) {
    //  const date = new Date(formData.CarDetail.issueDate);
      const date = formData.CarDetail.issueDate;
      setIssueDate(date);
    } else {
      setIssueDate(null);
    }
  
    setSelectedValues({
      scheme: formData.CarDetail.scheme || "",
      primaryStandardReference: formData.CarDetail.primaryStandardReference || "",
      status: formData.CarDetail.status || "Draft",
      submitClosedStatus: formData.CarDetail.submitClosedStatus || false
    });

    if (formData && !initialFormDataRef.current) {
      initialFormDataRef.current = formData;
    }

  }, [formData]);  
  
  const handleInputChange = (field: string, value: any) => {
    if(field === "siteIssuedTo") {
      const fieldValue = value?.value || value
      if(!fieldValue) return;
      let siteIssuedTo = formData.CarDetail.siteIssuedTo
      if(formData.CarDetail.siteIssuedTo?.split(",").filter(Boolean).includes(fieldValue)){
        siteIssuedTo = formData.CarDetail.siteIssuedTo?.split(",").filter((site: string) => site !== fieldValue).join(",")
      } else {
        siteIssuedTo = Boolean(formData.CarDetail.siteIssuedTo) ? `${formData.CarDetail.siteIssuedTo},${fieldValue}` : fieldValue;
      }
      
      setFormData((prevFormData: { CarDetail: any }) => ({
        ...prevFormData,
        CarDetail: {
          ...prevFormData.CarDetail,
          siteIssuedTo,
        },
      }));
    } else {
      setFormData((prevFormData: { CarDetail: any }) => ({
        ...prevFormData,
        CarDetail: {
          ...prevFormData.CarDetail,
          [field]: value,
        },
      }));
    }
  };

  const handleCheckboxChange = (checked: boolean) => {
    setSelectedValues((prevValues) => {
      const newStatus = checked ? "Closed" : "Draft"; // Set status based on checkbox state
  
      const updatedValues = {
        ...prevValues,
        status: newStatus,
        submitClosedStatus: checked, // Update checkbox state
      };
  
      setFormData((prevFormData: { CarDetail: any }) => ({
        ...prevFormData,
        CarDetail: {
          ...prevFormData.CarDetail,
          status: newStatus, // Update status in formData
        },
      }));
  
      return updatedValues;
    });
  };
  
  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Format the date to 'YYYY-MM-DD'
      const formattedDate = date.toISOString().split('T')[0]; 
      setIssueDate(formattedDate);
      setFormData((prevFormData: { CarDetail: any }) => ({
        ...prevFormData,
        CarDetail: {
          ...prevFormData.CarDetail,
          issueDate: formattedDate,
        },
      }));
    }
  };

  const siteNameOptions = !!certifiedSiteData.length ? certifiedSiteData : formData.CarDetail.siteIssuedTo?.split(",").filter(Boolean).map((site: string) => ({ id: site, label: site, value: site }))

  return (
    <Form className="p-3">
      <Row className="align-items-center mb-3">
        {/* Scheme */}
        <Col xs={12} md={4}>
          <Form.Group className="cardetails-form" controlId="scheme">
            <Form.Label className="w-50" style={{marginTop: "5px"}}>Scheme</Form.Label>
            <SelectField
            label="Select Scope"
            data={option.scheme}
            disabled = {disabled}
            selectedValue={option.scheme.find(item => item.value === selectedValues.scheme)?.label || ""}
            onChange={(value) => handleSelectChange("scheme", value)}
          />


          </Form.Group>
        </Col>

        {/* Issue Date */}
        <Col xs={12} md={4}>
          <Form.Group className="cardetails-form" controlId="issueDate">
            <Form.Label className="w-50" style={{marginTop: "5px"}}>Issue Date</Form.Label>
            <div className="pl-3 position-relative">
              <DatePicker
                selected={issueDate}
                onChange={handleDateChange}
                className="custom-datepicker car-custom-datepicker"
                placeholderText="Select date"
                disabled = {disabled}
              />
              <img
                className="calendar-icon car-calendar-icon"
                src={calendarIcon}
                alt="Calendar Icon"
              />
            </div>
          </Form.Group>
        </Col>

        {/* Original Grade */}
        <Col xs={12} md={4}>
          <Form.Group className="cardetails-form" controlId="grade">
            <Form.Label className="w-100">Original Grade</Form.Label>
            <Form.Control
              plaintext
              readOnly
              defaultValue={
                formData.CarDetail.grade
                  ? formData.CarDetail.grade === "Observation"
                    ? formData.CarDetail.grade
                    : `${formData.CarDetail.grade} CAR`
                  : "--"
              }
              style={{
                color:
                  formData.CarDetail.grade === "Major"
                    ? "#C43131"
                    : formData.CarDetail.grade === "Minor"
                    ? "#F49632"
                    : formData.CarDetail.grade === "Observation"
                    ? "#919191"
                    : "inherit",
                fontSize: "16px",
                fontWeight: "800",
                padding: "0",
              }}
            />
          </Form.Group>
        </Col>



      </Row>

      <Row className="align-items-center mb-3">
        {/* Primary Standard Reference */}
        <Col xs={12} md={4}>
          <Form.Group className="cardetails-form car-form-primary-reference" controlId="primaryReference">
            <Form.Label className="w-50">Primary Standard Reference</Form.Label>
            <SelectField
              label="FSC-xx-xx-xx"
              data={option.primaryStandardReference}
              disabled = {disabled}
              selectedValue={option.primaryStandardReference.find(item => item.value === selectedValues.primaryStandardReference)?.label || ""}
              onChange={(value) => handleSelectChange("primaryStandardReference", value)}
            />
          </Form.Group>
        </Col>

         {/* Section(s) */}
         <Col xs={12} md={4}>
          <Form.Group className="cardetails-form" controlId="sections">
            <Form.Label className="w-50" style={{marginTop: "5px"}}>Section(s)</Form.Label>
            <Form.Control
              type="text"
              placeholder="Section"
              style={{ width: "60%", height: "40px" }}
              value={formData.CarDetail.section}
              disabled = {disabled}
              onChange={(e) => handleInputChange("section", e.target.value)}
            />
          </Form.Group>
        </Col>

      </Row>

      <Row className="align-items-center mb-3">
       
       {/* Submit CAR finding with Closed Status Checkbox */}
       <Col xs={12} md={4}>
        <Form.Group className="cardetails-form" controlId="submitClosedStatus">
          <div className="d-flex align-items-center justify-content-center gap-2">
          <Form.Check
              type="checkbox"
              checked={selectedValues.status === "Closed"} 
              onChange={(e) => handleCheckboxChange(e.target.checked)}
              disabled={disabled || (initialFormDataRef?.current?.CarDetail?.status === "Closed" && selectedValues.status === "Closed")}
            />
            <Form.Label style={{ margin: 0 }}>
              Submit CAR finding with Closed Status
            </Form.Label>
          </div>
        </Form.Group>
      </Col>

        {/* Site issued to */}
        <Col xs={12} md={4}>
          <Form.Group className="cardetails-form car-form-site-issue" controlId="siteIssuedTo">
            <Form.Label className="w-50" style={{ marginRight: "4px", marginTop: "8px" }}>Site issued to</Form.Label>
            <CommonSelectField
              label="Site name"
              data={siteNameOptions}
              multiselect
              disabled = {disabled}
              selectedValue={formData.CarDetail.siteIssuedTo?.split(",").filter(Boolean)}
              onChange={(_, option) =>
                handleInputChange("siteIssuedTo", option)
              }
            />
          </Form.Group>
        </Col>
        {carData?.Status__c === "Closed" &&
          <Col xs={12} md={4}>
              <strong className="d-flex align-items-center gap-2 w-100">
                Closed Date: {carData.Closed_Date__c ? dateConversion(carData.Closed_Date__c, "DD MMMM YYYY") : "--"}
              </strong>
          </Col>
        }
      </Row>
    </Form>
  );
};

export default CarDetail;
